import { buffCountdownFresh, manualTimerSettings, type BuffDefinition, type BuffObservation } from './types'

interface ManualTimer { end: number; seconds: number; cycle: number; evidenceAt: number; recognizedCycle: number }

/** Reliable game readings calibrate deadlines; the monitoring clock carries them through gaps. */
export class ManualBuffTimers {
  private timers = new Map<string, ManualTimer>()
  private sequence = 0
  get active(): boolean { return this.timers.size > 0 }
  reset(): void { this.timers.clear() }

  apply(definitions: BuffDefinition[], recognized: BuffObservation[], now: number): BuffObservation[] {
    const enabled = new Set(definitions.filter(item => item.enabled && manualTimerSettings(item).enabled).map(item => item.id))
    for (const id of this.timers.keys()) if (!enabled.has(id)) this.timers.delete(id)
    return definitions.flatMap(definition => {
      const observation = recognized.find(item => item.id === definition.id)
      if (!enabled.has(definition.id)) return observation ? [observation] : []
      const settings = manualTimerSettings(definition)
      let timer = this.timers.get(definition.id)
      if (timer && timer.seconds !== settings.seconds) {
        timer.seconds = settings.seconds; timer.end = now + settings.seconds * 1000; timer.cycle = ++this.sequence
        // A saved duration wins over any frame captured before the restart.
        timer.evidenceAt = now
      }
      if (observation && buffCountdownFresh(observation, now) && !observation.held && !observation.predicted && !observation.numberHidden
        && observation.timerSource !== 'manual' && observation.estimatedEndAt !== undefined && Number.isFinite(observation.estimatedEndAt) && observation.estimatedEndAt > now
        && observation.numberEvidenceAt !== undefined && Number.isFinite(observation.numberEvidenceAt) && observation.numberEvidenceAt <= now && now <= (observation.numberValidUntil ?? -Infinity)
        && (!timer || observation.numberEvidenceAt > timer.evidenceAt)
        && observation.timeReason !== 'number-conflict-pending' && observation.timeReason !== 'renewal-confirming') {
        if (!timer) {
          timer = { end: observation.estimatedEndAt, seconds: settings.seconds, cycle: ++this.sequence,
            evidenceAt: observation.numberEvidenceAt, recognizedCycle: observation.cycle }
          this.timers.set(definition.id, timer)
        } else {
          if (timer.recognizedCycle !== observation.cycle) timer.cycle = ++this.sequence
          timer.end = observation.estimatedEndAt; timer.evidenceAt = observation.numberEvidenceAt; timer.recognizedCycle = observation.cycle
        }
      }
      if (!timer) return observation ? [observation] : []
      if (now >= timer.end) {
        const period = timer.seconds * 1000
        timer.end += (Math.floor((now - timer.end) / period) + 1) * period
        timer.cycle = ++this.sequence
      }
      const seconds = (timer.end - now) / 1000
      return [{ id: definition.id, timerSource: 'manual', presence: 'present', seconds, upperSeconds: seconds,
        capturedAt: now, validUntil: timer.end, estimatedEndAt: timer.end, cycle: timer.cycle, reason: 'manual-timer' }]
    })
  }
}
